import { Alert, Button, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { SectionCard } from "./ui";
import { Page } from "../shell/Page";

/**
 * Stops one bad render from blanking the whole app.
 *
 * Without this, a single unexpected shape - a field the server stopped
 * sending, a null where an object was assumed - throws during render and React
 * unmounts everything, leaving a white page with no heading, no message and no
 * way back. That happened: POST /bills briefly answered without `payments`,
 * and reading `.length` off it took out the entire screen rather than one row.
 *
 * The server fault is fixed, but "any render error destroys the session" is
 * the wrong failure mode for staff who are mid-sale with a customer waiting.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | undefined }
> {
  override state: { error: Error | undefined } = { error: undefined };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The console is the only log the browser gives us; keep the component
    // trace, which is what actually identifies the offending screen.
    console.error("Render failed:", error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Page
        title="This screen could not be drawn"
        subtitle="Nothing you did is lost — anything already saved is saved."
      >
        <SectionCard>
          <Stack gap="sm">
            <Alert
              variant="light"
              color="red"
              radius="md"
              icon={<IconAlertTriangle size={18} stroke={1.8} />}
            >
              {error.message}
            </Alert>
            <Text fz="md" c="gray.7">
              If you had just generated a bill or confirmed an order, it may
              well have gone through. Check the list before doing it again.
            </Text>
            <Button size="md" onClick={() => this.setState({ error: undefined })}>
              Try this screen again
            </Button>
            {/* A real link, not a router navigation: the router is inside the
                subtree that just failed, and the point of this button is to
                get out of it. */}
            <Button component="a" href="/" variant="default" size="md">
              Start from the home screen
            </Button>
          </Stack>
        </SectionCard>
      </Page>
    );
  }
}
